import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { BaseHttpService } from './base-http.service';
import {
  PaymentHistory,
  PaymentRequest,
  PaymentResponse,
  PremiumSchedule,
  IHPaymentMethodDto,
  ApiResponse,
  IHPolicyForPayPremiumDto,
  VoucherValidationRequest,
  VoucherValidationResponse
} from '../models';

/**
 * Payment Service for IH API
 * Handles premium payments, payment history, and voucher validation
 */
@Injectable({
  providedIn: 'root'
})
export class PaymentService extends BaseHttpService {
  private readonly PAYMENT_ENDPOINT = 'payment';

  /**
   * Get payment history
   * GET /api/ih/payment/history/{phone}
   */
  getPaymentHistory(phone: string): Observable<PaymentHistory[]> {
    return this.get<PaymentHistory[]>(`${this.PAYMENT_ENDPOINT}/history/${phone}`);
  }

  /**
   * Get payment history by policy
   * GET /api/ih/payment/history/{phone}/{policyNo}
   */
  getPaymentHistoryByPolicy(phone: string, policyNo: string): Observable<PaymentHistory[]> {
    return this.get<PaymentHistory[]>(`${this.PAYMENT_ENDPOINT}/history/${phone}/${policyNo}`);
  }

  /**
   * Process premium payment
   * POST /api/ih/payment/premium
   */
  processPremiumPayment(request: PaymentRequest): Observable<PaymentResponse> {
    return this.post<PaymentResponse>(`${this.PAYMENT_ENDPOINT}/premium`, request);
  }

  /**
   * Get premium schedule with filters and pagination
   * POST /api/ih/coverages/premium-schedule
   * 
   * @param params - Filter parameters
   * @returns Observable with count and data list
   */
  getPremiumScheduleWithFilters(params: {
    phone: string;
    policyNo: string;
    statusId?: number;      // -1=All, 0=Unpaid, 1=Paid, 2=Cancelled
    startDate?: string;
    endDate?: string;
    pageNum?: number;
    pageSize?: number;
  }): Observable<{count: number; data: PremiumSchedule[]}> {
    // Map to backend parameter names
    const body: any = {
      IHPhone: params.phone,
      plNo: params.policyNo,
      paymentStatusId: params.statusId ?? -1,
      pgNum: params.pageNum ?? 1,
      pgSz: params.pageSize ?? 10
    };
    
    // Optional date filters
    if (params.startDate) {
      body.startDt = params.startDate;
    }
    if (params.endDate) {
      body.endDt = params.endDate;
    }
    
    return this.post<any>('coverages/premium-schedule', body)
      .pipe(
        map(response => ({
          count: response.count || 0,
          data: response.dataList || []
        })),
        catchError(error => {
          console.error('Error fetching premium schedule:', error);
          return of({count: 0, data: []});
        })
      );
  }
  
  /**
   * Get premium schedule (legacy method - kept for backward compatibility)
   * GET /api/ih/payment/premium-schedule/{policyNo}
   * @deprecated Use getPremiumScheduleWithFilters instead
   */
  getPremiumSchedule(policyNo: string): Observable<PremiumSchedule[]> {
    return this.get<PremiumSchedule[]>(`${this.PAYMENT_ENDPOINT}/premium-schedule/${policyNo}`);
  }

  /**
   * Get next premium due
   * GET /api/ih/payment/next-due/{policyNo}
   */
  getNextPremiumDue(policyNo: string): Observable<PremiumSchedule> {
    return this.get<PremiumSchedule>(`${this.PAYMENT_ENDPOINT}/next-due/${policyNo}`);
  }

  /**
   * Validate payment amount
   * GET /api/ih/payment/validate-amount/{policyNo}/{amount}
   */
  validatePaymentAmount(policyNo: string, amount: number): Observable<boolean> {
    return this.getRaw<boolean>(`${this.PAYMENT_ENDPOINT}/validate-amount/${policyNo}/${amount}`);
  }

  /**
   * Get available payment methods
   * GET /api/ih/payment/methods
   */
  getPaymentMethods(): Observable<IHPaymentMethodDto[]> {
    return this.get<IHPaymentMethodDto[]>(`${this.PAYMENT_ENDPOINT}/methods`);
  }

  getPolicyForPayment(ihPhone: string, policyNo: string): Observable<IHPolicyForPayPremiumDto> {
    return this.get<IHPolicyForPayPremiumDto>(`${this.PAYMENT_ENDPOINT}/policy-for-premium/${policyNo}/${ihPhone}`);
  }

  // ============================================================================
  // Voucher Methods
  // ============================================================================

  /**
   * Validate a voucher code for premium payment
   * POST /api/ih/payment/voucher/validate
   * 
   * Note: This endpoint returns VoucherValidationResponse directly without ApiResponse wrapper,
   * so we use postRaw instead of post.
   * 
   * @param request - Voucher validation request with code, policyNo, premiumAmount
   * @returns Observable with validation result, voucher details, and discount calculation
   */
  validateVoucher(request: VoucherValidationRequest): Observable<VoucherValidationResponse> {
    return this.postRaw<VoucherValidationResponse>(`${this.PAYMENT_ENDPOINT}/voucher/validate`, request);
  }
}
